import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useNavigate } from "react-router";
import { css, html } from "react-strict-dom";
import * as Y from "yjs";
// Type-only: load zustand's StoreMutators module augmentations. Grid 0.9.2's
// public `GridStore` type is a zustand `Mutate<…>` over the
// "zustand/subscribeWithSelector" + "zustand/immer" mutator keys; without
// these augmentations in the program those keys don't exist and the store
// type collapses to `never` (every `useGridStore.getState()` then errors).
// One import anywhere in the program suffices (see the same shim in y-grid).
import type {} from "zustand/middleware";
import type {} from "zustand/middleware/immer";
import {
  useGridStore,
  DEFAULT_GRID_CONFIG,
  type GridState,
} from "@fredericrous/grid";

import { api, type MeResponse } from "../api/client.js";
import { publishSite } from "../publish/publishSite.js";
import { openPreview } from "../preview/openPreview.js";
import { Button, Tooltip } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import {
  apiErrorMessage,
  publishErrorMessage,
} from "../publish/publishError.js";
import { readSnapshot, gitRestore } from "../publish/gitClient.js";
import { parseSnapshot } from "../publish/snapshotCodec.js";
import { snapshotGridsToLayouts } from "./snapshotLayouts.js";
import { createSequencer } from "../util/sequencer.js";
import { LocalGitSession } from "../publish/localGit.js";
import { updateDraft } from "../local/draftStore.js";
import {
  adoptAssets,
  useEditorDoc,
  useStarterTemplate,
  SitemapCanvas,
  PreviewCanvas,
  AddDragProvider,
  AddSectionPalette,
  AddDragGhost,
  CrossTabDragCoach,
  MoveDragGhost,
  FormatToolbar,
  PanelDockGroup,
  MediaLibraryPanel,
  SelectionActionBar,
  EditorProvider,
  SelectFirstTip,
  ResizePrecisionTip,
  ResizeMetricsProvider,
  createPersistentSink,
  noopSink,
  type MetricsSink,
  SiteProvider,
  ThemeVarsProvider,
  isCollabRelayConfigured,
  resetGridStoreForDraft,
  setPaletteDesignSystemName,
  publishDockedInset,
} from "@website-builder/editor";
import { resolveTokens, duro } from "@website-builder/theme-engine";
import { brandToDuroVars } from "../theme/duroTheme.js";
// Pure crypto subpath — no libp2p in the bundle; the live node is lazy-loaded
// by useEditorDoc only once a room link is present.

// Local-only resize baseline. Created once at module scope so the counters are
// not reset by a re-render, and guarded because localStorage throws outright in
// some privacy modes — a metric must never be able to break the editor.
// Read it from the console with:
//   JSON.parse(localStorage.getItem("wb.resizeMetrics.v1"))
const resizeMetricsSink: MetricsSink = (() => {
  try {
    if (typeof window === "undefined") return noopSink;
    return createPersistentSink(window.localStorage).sink;
  } catch {
    return noopSink;
  }
})();
import { mintRoomLink } from "@website-builder/p2p-collab/roomCrypto";
import {
  readNavNested,
  writeNavNested,
  writeSiteTheme,
  subscribeSiteSettings,
  createSiteThemeSnapshot,
} from "./siteSettings.js";
import type { SiteTheme } from "@website-builder/schemas";
import { SiteSettingsDrawer } from "./SiteSettingsDrawer.js";
import { UsageIndicator } from "./UsageIndicator.js";
import { relativeTime } from "../util/relativeTime.js";
import {
  MultiEditorProvider,
  useFloatingAnchor,
} from "@fredericrous/lexical-multi";
import { createPortal } from "react-dom";
import { DraftsDialog } from "./DraftsDialog.js";
import { PublishDialog } from "./PublishDialog.js";
import { WidgetInspector } from "./WidgetInspector.js";
import { QuickEditCard } from "./QuickEditCard.js";
import { DesignSystemDrawer } from "./DesignSystemTab.js";
import { CanvasDesignSystemProvider } from "../theme/canvasDesignSystem.js";
import {
  useActiveDesignSystem,
  type DesignSystemState,
} from "../designSystem/useActiveDesignSystem.js";
import type { ActiveDesignSystem } from "../designSystem/designSystemFlows.js";
import { layers } from "@duro-app/tokens/tokens/layers.css";

const PUBLIC_HOST =
  (import.meta.env as { VITE_PUBLIC_HOST?: string }).VITE_PUBLIC_HOST ??
  "builder.daddyshome.fr";

// Longhand only, per react-strict-dom. Design values come from the deep
// token imports (the builder-webapp vitest config compiles them too).
const styles = css.create({
  // Zero-size, fixed, unscaled anchor — see FloatingToolbarAnchor.
  floatingAnchor: {
    position: "fixed",
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    zIndex: layers.overlay,
  },
  screen: {
    fontFamily: "system-ui, sans-serif",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    paddingTop: "0.75rem",
    paddingBottom: "0.75rem",
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
    flexShrink: 0,
  },
  chromeButton: {
    color: colors.text,
    backgroundColor: "transparent",
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: "0.375rem",
    paddingTop: "0.375rem",
    paddingBottom: "0.375rem",
    paddingLeft: "0.625rem",
    paddingRight: "0.625rem",
    cursor: "pointer",
    fontSize: "0.8rem",
  },
  siteName: {
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    fontSize: typography.fontSizeMd,
    fontWeight: typography.fontWeightSemibold,
  },
  slug: {
    fontSize: typography.fontSizeXs,
    color: colors.textMuted,
    backgroundColor: colors.bgCard,
    paddingTop: "0.125rem",
    paddingBottom: "0.125rem",
    paddingLeft: "0.375rem",
    paddingRight: "0.375rem",
    borderRadius: "0.25rem",
  },
  meta: {
    fontSize: typography.fontSizeXs,
    color: colors.textMuted,
  },
  headerActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  error: {
    color: colors.error,
    fontSize: "0.8rem",
  },
  liveLink: {
    fontSize: "0.8rem",
    color: colors.accent,
  },
  publishButton: {
    paddingTop: "0.375rem",
    paddingBottom: "0.375rem",
    paddingLeft: "0.875rem",
    paddingRight: "0.875rem",
    borderWidth: 0,
    borderStyle: "none",
    borderRadius: "0.375rem",
    fontSize: typography.fontSizeSm,
  },
  publishTone: (enabled: boolean) => ({
    backgroundColor: enabled ? "rgb(17, 24, 39)" : "rgb(209, 213, 219)",
    color: enabled ? "rgb(255, 255, 255)" : "rgb(156, 163, 175)",
    cursor: enabled ? "pointer" : "not-allowed",
  }),
  previewBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    backgroundColor: colors.warningBg,
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.warningBorder,
    fontSize: "0.8rem",
    color: colors.warning,
    flexShrink: 0,
  },
  previewActions: {
    marginLeft: "auto",
    display: "flex",
    gap: "0.5rem",
  },
  restoreButton: {
    paddingTop: "0.25rem",
    paddingBottom: "0.25rem",
    paddingLeft: "0.75rem",
    paddingRight: "0.75rem",
    backgroundColor: colors.accent,
    color: colors.accentContrast,
    borderWidth: 0,
    borderStyle: "none",
    borderRadius: "0.375rem",
    cursor: "pointer",
    fontSize: "0.8rem",
    fontWeight: typography.fontWeightSemibold,
  },
  exitPreviewButton: {
    paddingTop: "0.25rem",
    paddingBottom: "0.25rem",
    paddingLeft: "0.75rem",
    paddingRight: "0.75rem",
    backgroundColor: "transparent",
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.warning,
    borderRadius: "0.375rem",
    cursor: "pointer",
    fontSize: "0.8rem",
    color: colors.warning,
  },
  // The canvas and, beside it, the docked widget Inspector (non-modal: the
  // canvas narrows but stays live).
  workArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "row",
  },
  inspectorSlot: {
    position: "relative",
    zIndex: layers.modalRaised,
    display: "flex",
  },
  loading: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.textMuted,
    fontSize: typography.fontSizeSm,
  },
});

type PreviewState =
  | null
  | { kind: "loading"; oid: string; label: string; committedAt: number }
  | {
      kind: "ready";
      oid: string;
      label: string;
      committedAt: number;
      ydoc: Y.Doc;
    }
  | { kind: "unavailable"; oid: string; label: string; committedAt: number };

/**
 * What the editor is editing. A `local` source is an account-free draft
 * (id = `local-…`, doc + git live only in this browser); a `cloud` source is
 * a server-owned site reached at /sites/:id. A local draft that's been put
 * online carries `cloudSiteId`/`cloudSlug` but stays local-backed.
 */
export interface EditorSource {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly role: "owner" | "editor" | "viewer";
  readonly isLocal: boolean;
  readonly templateId?: string;
  readonly cloudSiteId?: string;
  readonly cloudSlug?: string;
}

export interface EditorScreenProps {
  readonly source: EditorSource;
  readonly session: MeResponse | null;
  /** Arrived back from sign-in to finish an online publish (?publish=<id>). */
  readonly resumePublish?: boolean;
}

/** Idle window before an edit is committed to the draft's local git history. */
const AUTO_COMMIT_IDLE_MS = 8000;

/**
 * One shared, top-level anchor for every text editor's floating selection
 * toolbar. The editors live inside the xyflow-transformed canvas, so each
 * editor's own inner-div anchor clips the toolbar and (the node being scaled)
 * mis-positions it. We portal a single FIXED, UNSCALED, zero-size anchor to
 * <body> and feed it to MultiEditorProvider via useFloatingAnchor — so every
 * editor's floating toolbar renders above everything and lands on the
 * selection. Zero-size + portaled to body so it never intercepts canvas
 * pointer events and escapes any transformed ancestor.
 */
function FloatingToolbarAnchor() {
  const { setFloatingAnchorElem } = useFloatingAnchor();
  return createPortal(
    <html.div ref={setFloatingAnchorElem} style={styles.floatingAnchor} />,
    document.body,
  );
}

export function EditorScreen({
  source,
  session,
  resumePublish = false,
}: EditorScreenProps) {
  const navigate = useNavigate();
  const [published, setPublished] = useState<{ url: string } | null>(
    source.cloudSlug
      ? { url: `https://${source.cloudSlug}.${PUBLIC_HOST}` }
      : null,
  );
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [designSystemOpen, setDesignSystemOpen] = useState(false);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [preview, setPreview] = useState<PreviewState>(null);
  // Cloud link state — seeded from the source, updated on first online publish
  // so a re-publish reuses the same cloud site instead of minting a new one.
  const [cloud, setCloud] = useState<{ id: string; slug: string } | null>(
    source.cloudSiteId
      ? { id: source.cloudSiteId, slug: source.cloudSlug ?? source.slug }
      : null,
  );
  const [currentSlug, setCurrentSlug] = useState(source.slug);
  useEffect(() => setCurrentSlug(source.slug), [source.slug]);

  // The docked Inspector's width, for the selection bar to centre beside it.
  // The slot is 0 wide when the Inspector is closed, or a drawer on a phone.
  const inspectorSlotRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = inspectorSlotRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      publishDockedInset(el.getBoundingClientRect().width),
    );
    ro.observe(el);
    return () => {
      ro.disconnect();
      publishDockedInset(0);
    };
  }, [preview]);

  // THE draft-switch reset for the singleton grid store — the cross-draft
  // bleed fix, hoisted from the per-grid reset-before-bind ceremonies that
  // used to live in usePageNode/sharedRegions (see draftGridIsolation.test.ts).
  // The screen is keyed by source.id, so this runs exactly once per draft
  // open, and the ordering is load-bearing: as a mount EFFECT it fires AFTER
  // the previous draft's canvas cleanup destroyed its store→Yjs bindings
  // (wiping the store while they're live would mirror delete-everything into
  // the OLD draft's doc) and BEFORE this draft's canvas mounts (gated on
  // editorDoc.loaded, which only flips in a later render) — so every grid this
  // draft binds starts from a pristine store and its Y.Doc is the sole source
  // of truth.
  useEffect(() => {
    resetGridStoreForDraft();
  }, []);

  // Cloud sites are owner-publishable; local drafts are always publishable.
  const canPublish = source.isLocal || source.role === "owner";
  // The site's content design system, as the server authorizes it (cloud
  // sites only — a local draft has no pointer and renders in Duro).
  const designSystem = useActiveDesignSystem(
    source.isLocal ? null : source.id,
    {
      isOwner: source.role === "owner",
    },
  );
  const buildDesignSystem = forBuild(designSystem.state);
  // The Add menu names its design-system group after the site's own.
  const dsName =
    designSystem.state.kind === "custom"
      ? designSystem.state.manifest.name
      : "Duro";
  useEffect(() => setPaletteDesignSystemName(dsName), [dsName]);
  // Build identity → cross-tab leader election picks the newest tab to own OPFS
  // persistence (and migrations). Injected by Vite define (see vite.config.ts).
  const editorDoc = useEditorDoc(source.id, {
    semver: __WB_VERSION__,
    build: __WB_BUILD__,
  });
  // PS1: a brand-new site opens pre-populated from its starter template
  // (bundled Yjs snapshot); the canvas waits the one frame it takes to apply.
  const templateReady = useStarterTemplate(editorDoc, source.templateId);

  // A draft's images live in this browser under the draft's own id, whether
  // or not it is online. Editors before 0.2.25 stored uploads under the cloud
  // id once the draft went online, where neither the canvas of a reopened
  // draft nor publish looked; adopt any of those before the canvas renders.
  const strayAssetsId =
    source.cloudSiteId && source.cloudSiteId !== source.id
      ? source.cloudSiteId
      : null;
  const [assetsAdopted, setAssetsAdopted] = useState(strayAssetsId === null);
  useEffect(() => {
    if (strayAssetsId === null) return;
    let cancelled = false;
    void adoptAssets(strayAssetsId, source.id)
      .catch((e: unknown) => console.warn("adopting draft assets failed", e))
      .finally(() => {
        if (!cancelled) setAssetsAdopted(true);
      });
    return () => {
      cancelled = true;
    };
  }, [strayAssetsId, source.id]);

  // ── Share to collaborate (free mode, P2P) ────────────────────────
  // Only offered for local drafts once a relay is configured. Minting a link
  // and dropping its #room&k fragment into the URL makes THIS tab join the room
  // too (useEditorDoc listens for hashchange); the copied link lets a peer join.
  const [shareCopied, setShareCopied] = useState(false);
  const canShare = source.isLocal && isCollabRelayConfigured();
  const onShare = useCallback(async () => {
    // roomId = siteId so the room's topic matches this draft's doc.
    const { link } = mintRoomLink(
      source.id,
      `${location.origin}${location.pathname}`,
    );
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      /* clipboard blocked — the hash below still activates this tab */
    }
    location.hash = link.slice(link.indexOf("#") + 1);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  }, [source.id]);

  // Site settings are DERIVED from the doc, never mirrored into React state.
  // The doc is written by this tab, by P2P peers, by other tabs over the sync
  // bridge, and by undo — a mirrored copy only tracked the first of those, so
  // the Brand drawer could show a theme that publish would not use.
  const subscribeSite = useCallback(
    (onChange: () => void) => subscribeSiteSettings(editorDoc.ydoc, onChange),
    [editorDoc.ydoc],
  );
  const getNavNested = useCallback(
    () => readNavNested(editorDoc.ydoc),
    [editorDoc.ydoc],
  );
  const navNested = useSyncExternalStore(
    subscribeSite,
    getNavNested,
    getNavNested,
  );
  const onNavNestedChange = (next: boolean) =>
    writeNavNested(editorDoc.ydoc, next);

  // Brand theme: the editable (effective) SiteTheme, derived + identity-cached
  // so getSnapshot is stable (see createSiteThemeSnapshot).
  const getSiteTheme = useMemo(
    () => createSiteThemeSnapshot(editorDoc.ydoc),
    [editorDoc.ydoc],
  );
  const siteTheme = useSyncExternalStore(
    subscribeSite,
    getSiteTheme,
    getSiteTheme,
  );
  const onThemeChange = (next: SiteTheme) =>
    writeSiteTheme(editorDoc.ydoc, next);
  // Resolved theme for the live canvas — matches the published render. `--wb-*`
  // vars feed the grid widgets; the Duro CSS-var override themes the real Duro
  // primitives (button/heading/card) placed on a page. Both ride the
  // ThemeVarsProvider, which page bodies and drag ghosts spread — never the
  // work area, so the editor's own chrome keeps the fleet theme (ADR-0010).
  const themeVars = useMemo(() => {
    const r = resolveTokens(duro, siteTheme);
    return { ...r.cssVars, ...brandToDuroVars(r.tokens) };
  }, [siteTheme]);

  useEffect(() => {
    setPublished(
      source.cloudSlug
        ? { url: `https://${source.cloudSlug}.${PUBLIC_HOST}` }
        : null,
    );
    setError(null);
  }, [source.id, source.cloudSlug]);

  // ── Local git history (account-free drafts) ──────────────────────
  // One warm worker per draft: holds the OPFS repo open so the editor can
  // auto-commit on idle (building real history for the "download with
  // history" export). Cloud sites keep their history server-side.
  const gitRef = useRef<LocalGitSession | null>(null);
  useEffect(() => {
    if (!source.isLocal) return;
    const session = new LocalGitSession(source.id);
    gitRef.current = session;
    return () => {
      session.dispose();
      gitRef.current = null;
    };
  }, [source.id, source.isLocal]);

  const buildInput = useCallback(
    () => ({
      ydoc: editorDoc.ydoc,
      siteId: cloud?.id ?? source.id,
      siteName: source.name,
    }),
    [editorDoc.ydoc, cloud?.id, source.id, source.name],
  );

  // Debounced auto-commit to the local repo on edit activity.
  useEffect(() => {
    if (!source.isLocal || !editorDoc.loaded) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void gitRef.current?.commit(
          buildInput(),
          `Edit ${new Date().toISOString()}`,
        );
        updateDraft(source.id, {});
      }, AUTO_COMMIT_IDLE_MS);
    };
    editorDoc.ydoc.on("update", schedule);
    return () => {
      if (timer) clearTimeout(timer);
      editorDoc.ydoc.off("update", schedule);
    };
  }, [source.isLocal, source.id, editorDoc.loaded, editorDoc.ydoc, buildInput]);

  // ── Version preview (read-only, cloud history) ───────────────────
  // Every enter/exit bumps this. `enterPreview` awaits a worker round-trip
  // before it writes to the SINGLETON grid store, and by then the user may
  // have exited (live canvas remounted, its y-grid→Yjs mirrors bound) or
  // started a different preview. Importing the stale snapshot's layouts at
  // that point mirrors them into the LIVE draft's doc and persists them —
  // silent data loss. Every continuation past an await re-checks this.
  const previewSeq = useRef(createSequencer()).current;

  async function enterPreview(
    oid: string,
    label: string,
    committedAt: number,
  ): Promise<void> {
    setSettingsOpen(false);
    setError(null);
    setPreview({ kind: "loading", oid, label, committedAt });
    const isCurrent = previewSeq.start();
    let r: Awaited<ReturnType<typeof readSnapshot>>;
    try {
      r = await readSnapshot(cloud?.id ?? source.id, oid);
    } catch (e) {
      // A worker that fails to boot rejects; without this the banner sticks
      // on "Loading preview…" forever with no way back.
      if (!isCurrent()) return;
      setError(e instanceof Error ? e.message : "Couldn't load preview.");
      setPreview(null);
      return;
    }
    if (!isCurrent()) return; // exited / superseded mid-flight
    if (!r.ok) {
      setError(r.error ?? "Couldn't load preview.");
      setPreview(null);
      return;
    }
    const parsed = r.snapshot ? parseSnapshot(r.snapshot) : null;
    if (!parsed) {
      setPreview({ kind: "unavailable", oid, label, committedAt });
      return;
    }
    const doc = new Y.Doc();
    const yNodes = doc.getMap("nodes");
    const yEdges = doc.getMap("edges");
    for (const n of parsed.nodes) {
      const nid = (n as { id?: string }).id;
      if (nid) yNodes.set(nid, n);
    }
    for (const e of parsed.edges) {
      const eid = (e as { id?: string }).id;
      if (eid) yEdges.set(eid, e);
    }
    // Swap the singleton store to the snapshot in ONE atomic importLayouts
    // call: every current grid id goes in `removeGridIds` (deleted first,
    // inside the same set()) and the snapshot's grids are imported over them,
    // so subscribers see exactly one old→new transition — like the wholesale
    // setState swap this replaces. Deliberately NOT resetGridStore()-then-
    // import: the LIVE canvas is still mounted here (preview flips to "ready"
    // only below), and its y-grid store→Yjs mirrors would see the intermediate
    // EMPTY store and write delete-everything into the live draft's doc (the
    // unmount-wipe bug class). importLayouts also re-wires nested-grid parent
    // back-references from the items' nestedGridId links — what the old swap
    // carried over by hand via the snapshot's GridState fields.
    // snapshotGridsToLayouts also applies the grid 2.0 stored-document
    // migration — history holds configs written by older app versions.
    const store = useGridStore.getState();
    store.importLayouts(
      snapshotGridsToLayouts(parsed.grids as Record<string, GridState>),
      Object.keys(store.grids),
    );
    for (const n of parsed.nodes) {
      const nid = (n as { id?: string }).id;
      if (nid && !useGridStore.getState().grids[nid])
        store.importLayout(nid, { config: DEFAULT_GRID_CONFIG, items: [] });
    }
    setPreview({ kind: "ready", oid, label, committedAt, ydoc: doc });
  }

  function exitPreview(): void {
    // Invalidate any enterPreview still awaiting its snapshot — otherwise its
    // continuation imports that snapshot's grids into the store we are about
    // to hand back to the live canvas.
    previewSeq.invalidate();
    // Factory-reset drops the snapshot's grids (and any preview-time
    // interaction state); the live canvas remounts right after and rebinds
    // every grid from the live draft's Y.Doc (shell create + import).
    resetGridStoreForDraft();
    if (preview?.kind === "ready") preview.ydoc.destroy();
    setPreview(null);
  }

  async function restoreFromPreview(): Promise<void> {
    if (!preview) return;
    let r: Awaited<ReturnType<typeof gitRestore>>;
    try {
      r = await gitRestore(cloud?.id ?? source.id, preview.oid);
    } catch (e) {
      // Worker boot/crash rejects — without this the click looks like a no-op.
      setError(e instanceof Error ? e.message : "Restore failed.");
      return;
    }
    if (r.ok) {
      exitPreview();
      setPublished({ url: `https://${currentSlug}.${PUBLIC_HOST}` });
    } else {
      setError(
        r.rejected
          ? "Someone published in the meantime — exit and try again."
          : (r.error ?? "Restore failed."),
      );
    }
  }

  // ── Preview: the published output, built and served on this device ──
  // Opens the tab synchronously (inside the click) on a waiting page, then
  // builds; the waiting page moves to the new revision when it's written.
  function onPreviewSite() {
    const opened = openPreview({
      ydoc: editorDoc.ydoc,
      siteId: source.id,
      siteName: source.name,
      designSystem: buildDesignSystem,
    });
    if (!opened.tab)
      setError(
        `Your browser blocked the preview tab. Allow pop-ups for this site, or open ${location.origin}${opened.url}`,
      );
    opened.done.catch((e) => console.warn("[preview] build failed", e));
  }

  // ── Publish: cloud sites push directly; local drafts open the dialog
  // (download-with-history vs publish-online). ──────────────────────
  async function handlePublishCloud() {
    setPublishing(true);
    setError(null);
    try {
      const outcome = await publishSite({
        ydoc: editorDoc.ydoc,
        siteId: source.id,
        siteName: source.name,
        designSystem: buildDesignSystem,
      });
      if (outcome.ok) {
        setPublished({ url: `https://${currentSlug}.${PUBLIC_HOST}` });
        // Shown, not only logged: a warning is something that didn't ship.
        if (outcome.warnings.length > 0)
          setError(
            `Published, but not everything made it: ${outcome.warnings.join("; ")}`,
          );
      } else {
        // A sentence the author can act on; the raw git log stays in the
        // console warning above rather than in the header banner.
        setError(publishErrorMessage(outcome.failure));
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setPublishing(false);
    }
  }

  function onPublishClick() {
    if (source.isLocal) setPublishOpen(true);
    else void handlePublishCloud();
  }

  // Resume an online publish after returning from sign-in.
  useEffect(() => {
    if (resumePublish && source.isLocal && session) setPublishOpen(true);
  }, [resumePublish, source.isLocal, session]);

  // ── Publish-dialog actions (local drafts) ────────────────────────
  const onExport = useCallback(async () => {
    const git = gitRef.current;
    if (!git) return { ok: false, error: "local git unavailable" };
    return git.exportZip(buildInput(), `${currentSlug || "site"}.zip`);
  }, [buildInput, currentSlug]);

  const onPublishOnline = useCallback(async () => {
    try {
      let target = cloud;
      if (!target) {
        const created = await api.createSite({
          name: source.name,
          templateId: source.templateId ?? "blank",
        });
        target = { id: created.id, slug: created.slug };
        setCloud(target);
        updateDraft(source.id, {
          cloudSiteId: target.id,
          cloudSlug: target.slug,
        });
      }
      const outcome = await publishSite({
        ydoc: editorDoc.ydoc,
        siteId: target.id,
        siteName: source.name,
        assetSiteId: source.id,
      });
      if (!outcome.ok)
        return { ok: false, error: publishErrorMessage(outcome.failure) };
      const url = `https://${target.slug}.${PUBLIC_HOST}`;
      setPublished({ url });
      return { ok: true, url, warnings: outcome.warnings };
    } catch (e) {
      return { ok: false, error: apiErrorMessage(e) };
    }
  }, [cloud, editorDoc.ydoc, source.id, source.name, source.templateId]);

  const onSignInToPublish = useCallback(() => {
    // Carry intent across the openauth round-trip; on return we land back on
    // this draft with ?publish=<id> and auto-open the publish dialog.
    const returnTo = `/?publish=${encodeURIComponent(source.id)}`;
    window.location.assign(
      `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`,
    );
  }, [source.id]);

  return (
    <SiteProvider siteId={source.id}>
      <MultiEditorProvider>
        <FloatingToolbarAnchor />
        <html.main style={styles.screen}>
          <html.header style={styles.header}>
            <html.button
              type="button"
              onClick={() => setDraftsOpen(true)}
              style={styles.chromeButton}
            >
              ☰ {source.isLocal ? "Drafts" : "Sites"}
            </html.button>
            <html.h1 style={styles.siteName}>{source.name}</html.h1>
            <html.code style={styles.slug}>{currentSlug}</html.code>
            {source.isLocal ? (
              <html.span style={styles.meta}>
                {cloud ? "online" : "local draft"}
              </html.span>
            ) : (
              <html.span style={styles.meta}>{source.role}</html.span>
            )}
            <html.div style={styles.headerActions}>
              {session && <UsageIndicator me={session} />}
              <Tooltip.Root
                content={
                  source.isLocal
                    ? "Brand & theme"
                    : "Site settings (URL, Media, History, Brand)"
                }
                placement="bottom"
              >
                <Tooltip.Trigger>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setSettingsOpen(true)}
                  >
                    {source.isLocal ? "🎨 Brand" : "⚙ Settings"}
                  </Button>
                </Tooltip.Trigger>
              </Tooltip.Root>
              {error && <html.span style={styles.error}>{error}</html.span>}
              {published && (
                <html.a
                  href={published.url}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.liveLink}
                >
                  View live ↗
                </html.a>
              )}
              {canShare && (
                <Tooltip.Root
                  content="Copy a private link so others can edit this draft with you (peer-to-peer, no account)"
                  placement="bottom"
                >
                  <Tooltip.Trigger>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => void onShare()}
                    >
                      {shareCopied ? "✓ Link copied" : "🔗 Share"}
                    </Button>
                  </Tooltip.Trigger>
                </Tooltip.Root>
              )}
              {!source.isLocal && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setDesignSystemOpen(true)}
                >
                  {designSystem.state.kind === "needs-consent" ||
                  designSystem.state.kind === "unavailable"
                    ? "Design system ⚠"
                    : "Design system"}
                </Button>
              )}
              <Button
                variant="secondary"
                size="small"
                onClick={onPreviewSite}
                disabled={preview !== null}
              >
                Preview site
              </Button>
              <html.button
                type="button"
                onClick={onPublishClick}
                disabled={!canPublish || publishing || preview !== null}
                style={[styles.publishButton, styles.publishTone(canPublish)]}
              >
                {publishing ? "Publishing…" : "Publish"}
              </html.button>
            </html.div>
          </html.header>

          <FormatToolbar />

          {preview && (
            <html.div style={styles.previewBanner}>
              {preview.kind === "loading" ? (
                <html.span>Loading preview of {preview.label}…</html.span>
              ) : preview.kind === "unavailable" ? (
                <html.span>
                  Preview isn’t available for {preview.label} — it was published
                  before version previews were added.
                </html.span>
              ) : (
                <html.span>
                  👁 Previewing <html.strong>{preview.label}</html.strong> ·{" "}
                  {relativeTime(preview.committedAt)} — read-only.
                </html.span>
              )}
              <html.div style={styles.previewActions}>
                {preview.kind === "ready" && canPublish && (
                  <html.button
                    type="button"
                    onClick={() => void restoreFromPreview()}
                    style={styles.restoreButton}
                  >
                    Restore this version
                  </html.button>
                )}
                <html.button
                  type="button"
                  onClick={exitPreview}
                  style={styles.exitPreviewButton}
                >
                  Exit preview
                </html.button>
              </html.div>
            </html.div>
          )}

          <html.div style={styles.workArea}>
            {/* eslint-disable-next-line duro/no-raw-html-element -- SitemapCanvas finds this node by `[data-wb-scope]` and sets `--wb-vw` on it imperatively, which react-strict-dom's StyleX-only `style` would overwrite */}
            <div
              // Canvas scope — the element the canvas pins `--wb-vw` on for
              // fluid type; page bodies inherit it (see SitemapCanvas +
              // fluidType.ts). The brand itself is NOT spread here: it would
              // re-theme the editor chrome inside the work area.
              data-wb-scope="1"
              style={{
                flex: 1,
                minHeight: 0,
                position: "relative",
              }}
            >
              {preview?.kind === "ready" ? (
                <ThemeVarsProvider value={themeVars}>
                  <PreviewCanvas ydoc={preview.ydoc} />
                </ThemeVarsProvider>
              ) : editorDoc.loaded && templateReady && assetsAdopted ? (
                <CanvasDesignSystemProvider state={designSystem.state}>
                  <AddDragProvider>
                    {/* ThemeVarsProvider wraps the GHOSTS too: their content must
                    render under the same --wb-* typography context as the
                    page nodes, or the travelling card drifts from the item
                    it represents. */}
                    <ThemeVarsProvider value={themeVars}>
                      {/* EditorProvider so the floating panels + dock group (which
                      live outside SitemapCanvas's own provider) can read the
                      site's ydoc via context — e.g. MediaLibraryBody. */}
                      <EditorProvider value={editorDoc}>
                        {/* Instrumentation for the resize-ergonomics work. The
                        default sink DISCARDS everything — wiring it up is not
                        the same as switching telemetry on, and turning it on
                        should be a deliberate, separate decision. */}
                        <ResizeMetricsProvider sink={resizeMetricsSink}>
                          <SitemapCanvas editorDoc={editorDoc} />
                          <AddSectionPalette />
                          <PanelDockGroup />
                          <MediaLibraryPanel />
                          {/* The one selection-scoped surface — the DS ActionBar,
                        not a docked panel: it appears with the selection,
                        shows the verbs for WHAT is selected (widget /
                        section / multi) and leaves when it clears. */}
                          <SelectionActionBar />
                          <AddDragGhost />
                          {/* Cross-grid move ghost — MUST live here, outside the
                        canvas (xyflow node transforms are containing blocks for
                        its position:fixed); without it the dragged item vanishes
                        the moment it leaves its source page. */}
                          <MoveDragGhost />
                          <SelectFirstTip />
                          <ResizePrecisionTip />
                          <CrossTabDragCoach />
                        </ResizeMetricsProvider>
                      </EditorProvider>
                    </ThemeVarsProvider>
                  </AddDragProvider>
                </CanvasDesignSystemProvider>
              ) : (
                <html.div style={styles.loading}>Loading your site…</html.div>
              )}
            </div>
            {!preview && (
              // Above the floating panels (Media, Add: z 1000-1001), which
              // default to the window's right edge — where the Inspector docks.
              <html.div ref={inspectorSlotRef} style={styles.inspectorSlot}>
                <WidgetInspector />
              </html.div>
            )}
            {!preview && <QuickEditCard />}
          </html.div>

          {designSystemOpen && !source.isLocal && (
            <DesignSystemDrawer
              siteId={source.id}
              isOwner={source.role === "owner"}
              designSystem={designSystem}
              onClose={() => setDesignSystemOpen(false)}
            />
          )}
          {settingsOpen && (
            <SiteSettingsDrawer
              siteId={cloud?.id ?? source.id}
              currentSlug={currentSlug}
              publicHost={PUBLIC_HOST}
              canEdit={canPublish}
              cloudFeatures={!source.isLocal}
              onClose={() => setSettingsOpen(false)}
              onSlugChanged={(s) => setCurrentSlug(s)}
              onPreview={(oid, label, committedAt) =>
                void enterPreview(oid, label, committedAt)
              }
              navNested={navNested}
              onNavNestedChange={canPublish ? onNavNestedChange : undefined}
              theme={siteTheme}
              onThemeChange={canPublish ? onThemeChange : undefined}
            />
          )}

          <DraftsDialog
            open={draftsOpen}
            onClose={() => setDraftsOpen(false)}
            activeId={source.id}
            signedIn={session !== null}
            onOpenDraft={(id) => {
              setDraftsOpen(false);
              navigate(`/?draft=${encodeURIComponent(id)}`);
            }}
            onOpenCloudSite={(id) => {
              setDraftsOpen(false);
              navigate(`/sites/${encodeURIComponent(id)}`);
            }}
          />

          <PublishDialog
            open={publishOpen}
            onClose={() => setPublishOpen(false)}
            draftName={source.name}
            slug={cloud?.slug ?? currentSlug}
            publicHost={PUBLIC_HOST}
            signedIn={session !== null}
            alreadyOnline={cloud !== null}
            onExport={onExport}
            onPublishOnline={onPublishOnline}
            onSignInToPublish={onSignInToPublish}
          />
        </html.main>
      </MultiEditorProvider>
    </SiteProvider>
  );
}

/** What a build renders with: still loading counts as not loaded yet (a
 *  build must never fall back to Duro for a site that has its own). */
function forBuild(state: DesignSystemState): ActiveDesignSystem {
  return state.kind === "loading"
    ? {
        kind: "unavailable",
        reason: "it is still loading — try again in a moment",
      }
    : state;
}
